import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAddCorsDomain } from '../../hooks/gql/mutation/create/useAddCorsDomain';
import { useGetAllCorsDomains } from '../../hooks/gql/query/getAll/useGetAllCorsDomains';
import { gql } from '@apollo/client';
import { useDeleteCorsDomain } from '../../hooks/gql/mutation/delete/useDeleteCorsDomain';

const GET_CORS_DOMAINS = gql`
  query GetAllCorsDomains($input: PaginationInput!) {
    getAllCorsDomains(input: $input) {
      _id
      domain
      enabled
    }
  }
`;

const CorsPage = () => {
  const [newOrigin, setNewOrigin] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const { addCorsDomain } = useAddCorsDomain();
  const { deleteCorsDomain } = useDeleteCorsDomain();

  const {
    data: domains_data,
    loading: domains_dataLoading,
    refetch: domains_dataRefetch,
    error: domains_dataError,
  } = useGetAllCorsDomains(GET_CORS_DOMAINS, { page: 1, item_per_page: 100 });

  const domains = domains_data?.map((d) => d.domain) || [];

  const handleAddOrigin = async () => {
    setIsAdding(true);
    if (newOrigin && !domains.includes(newOrigin)) {
      await addCorsDomain({ domain: newOrigin });
      setNewOrigin('');
      await domains_dataRefetch();
    }
    setIsAdding(false);
  };

  const handleRemoveOrigin = async (originToRemove: string) => {
    setIsAdding(true);
    await deleteCorsDomain({ text: originToRemove });
    await domains_dataRefetch();
    setIsAdding(false);
  };

  return (
    <div className='py-12 px-4 sm:px-6 lg:px-8'>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className=' mx-auto'>
        <div className='bg-white rounded-2xl shadow-xl overflow-hidden'>
          {/* Header */}
          <div className=' px-6 py-8 '>
            <h1 className='text-xl font-bold'>CORS Configuration</h1>
            <p className='mt-2 opacity-90'>
              Manage your allowed origins for cross-origin requests from
              operators website using prebooked widgets.
            </p>
          </div>

          {/* Content */}
          <div className='p-6 space-y-8'>
            {/* Allowed Origins Section */}
            <div>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-semibold text-gray-800'>
                  Allowed Origins
                </h2>
                <span className='bg-green-100 text-[#47C56C] text-xs font-medium px-2.5 py-0.5 rounded-full'>
                  {domains.length} {domains.length === 1 ? 'origin' : 'origins'}
                </span>
              </div>

              {domains.length === 0 ? (
                <div className='rounded-lg p-4 text-center text-gray-500'>
                  No origins configured yet
                </div>
              ) : (
                <div className='flex flex-wrap gap-2'>
                  <AnimatePresence>
                    {domains.map((origin) => (
                      <motion.div
                        key={origin}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                        className='relative group'>
                        <div className='bg-green-100 text-green-500 rounded-full px-4 py-2 pr-8 flex items-center'>
                          <span className='text-sm font-medium truncate max-w-[180px]'>
                            {origin}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemoveOrigin(origin)}
                          className='absolute right-2 top-1/2 -translate-y-1/2 text-green-500 hover:text-green-400 transition-colors opacity-0 group-hover:opacity-100'
                          aria-label='Remove origin'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-4 w-4'
                            viewBox='0 0 20 20'
                            fill='currentColor'>
                            <path
                              fillRule='evenodd'
                              d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                              clipRule='evenodd'
                            />
                          </svg>
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Add Origin Section */}
            <div className='border-t border-gray-200 pt-6'>
              {!isAdding ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsAdding(true)}
                  className='w-full flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-700 text-white px-4 py-3 rounded-lg transition-colors duration-200'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-5 w-5'
                    viewBox='0 0 20 20'
                    fill='currentColor'>
                    <path
                      fillRule='evenodd'
                      d='M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z'
                      clipRule='evenodd'
                    />
                  </svg>
                  <span>Add New Origin</span>
                </motion.button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className='space-y-4'>
                  <h3 className='text-lg font-medium text-gray-800'>
                    Add New Allowed Origin
                  </h3>
                  <div className='flex space-x-3'>
                    <input
                      type='text'
                      className='flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all'
                      placeholder='https://yourdomain.com'
                      value={newOrigin}
                      onChange={(e) => setNewOrigin(e.target.value)}
                      autoFocus
                    />
                    <div className='flex space-x-2'>
                      <button
                        onClick={handleAddOrigin}
                        disabled={!newOrigin}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          newOrigin
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}>
                        Confirm
                      </button>
                      <button
                        onClick={() => {
                          setIsAdding(false);
                          setNewOrigin('');
                        }}
                        className='px-4 py-2 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors'>
                        Cancel
                      </button>
                    </div>
                  </div>
                  <p className='text-sm text-gray-500'>
                    Enter the full origin URL including protocol (e.g.,
                    https://example.com)
                  </p>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CorsPage;
